import { createFilterDescription } from "./filterDescription";
import { parseProductFilterOpen } from "../abProducts.config";

interface CheckboxFilterConfig<T> {
  label: string;
  eventName: string;
  optionLabel: (value: T) => string;
}

/** Shared rendering for listing filters whose options are typed checkbox values. */
export class CheckboxFilterElement<T extends string | number> extends HTMLElement {
  #options: readonly T[] = [];
  #selected: readonly T[] = [];
  #optionsEl?: HTMLElement;

  constructor(private readonly config: CheckboxFilterConfig<T>) {
    super();
  }

  connectedCallback() {
    if (this.#optionsEl) return;
    const details = document.createElement("details");
    details.open = parseProductFilterOpen(this);
    const title = document.createElement("summary");
    title.textContent = this.getAttribute("label") || this.config.label;
    this.#optionsEl = document.createElement("div");
    details.append(title);
    const description = createFilterDescription(this);
    if (description) details.append(description);
    details.append(this.#optionsEl);
    this.replaceChildren(details);
    this.#render();
  }

  update(options: readonly T[], selected: readonly T[]) {
    this.#options = options;
    this.#selected = selected;
    this.#render();
  }

  #render() {
    if (!this.#optionsEl) return;
    const currentValues = Array.from(this.#optionsEl.querySelectorAll<HTMLInputElement>("input"))
      .map((input) => input.value);
    if (currentValues.join("\0") !== this.#options.map(String).join("\0")) {
      this.#optionsEl.replaceChildren(...this.#options.map((value) => {
        const label = document.createElement("label");
        const input = document.createElement("input");
        input.type = "checkbox";
        input.value = String(value);
        input.addEventListener("change", () => {
          const checked = new Set(Array.from(this.#optionsEl!.querySelectorAll<HTMLInputElement>("input:checked"))
            .map((input) => input.value));
          this.dispatchEvent(new CustomEvent<readonly T[]>(this.config.eventName, {
            detail: this.#options.filter((option) => checked.has(String(option))), bubbles: true,
          }));
        });
        label.append(input, ` ${this.config.optionLabel(value)}`);
        return label;
      }));
    }
    const selected = new Set(this.#selected.map(String));
    this.#optionsEl.querySelectorAll<HTMLInputElement>("input").forEach((input) => {
      input.checked = selected.has(input.value);
    });
  }
}
